import { BaseService } from './base.service';
import { isDemoMode } from '@/demo/demoMode';
import type { PaymentIntentBooking, PaymentIntentItem } from './stripe.service';

export interface CreateCheckoutSessionRequest {
  /** Total in cents, as the Stripe path always sent it. */
  amount: number;
  currency: string;
  locationId: string;
  items?: PaymentIntentItem[];
  booking?: PaymentIntentBooking;
  /* What to create once the invoice is paid. The member is on
     SmartLev's page when the money moves and may never come back to
     this screen, so the server needs everything it takes to finish
     the purchase without us. */
  fulfilment?: Record<string, unknown>;
}

export type CheckoutSessionStatus =
  | 'PENDING'
  | 'PAID'
  | 'FULFILLED'
  | 'FULFILMENT_FAILED'
  | 'EXPIRED'
  | 'CANCELED';

export interface CheckoutSession {
  id: string;
  status: CheckoutSessionStatus;
  amount: number;
  currency: string;
  /** The SmartLev page to send the member to. */
  invoiceUrl?: string;
  ghlInvoiceNumber?: string;
  /** SmartLev was asked to charge a card already on file. */
  autoCharge?: boolean;
  /** The order or booking the payment produced. */
  fulfilledRef?: string;
  failureReason?: string;
  expiresAt: string;
}

/**
 * Checkout, with SmartLev taking the money.
 *
 * Replaces creating our own payment intent and charging the card in the app.
 * The reason is refunds: a charge we took ourselves and then recorded against
 * a SmartLev invoice is a manual transaction there, which SmartLev will not
 * void and has no API to refund — so a refund could never travel between the
 * two systems. A payment made on SmartLev's own page can be reversed from
 * either side.
 */
class CheckoutService extends BaseService {
  constructor() {
    super('/checkout');
  }

  async createSession(
    payload: CreateCheckoutSessionRequest,
  ): Promise<CheckoutSession> {
    return this.post<CheckoutSession>('/sessions', payload);
  }

  /**
   * @param visible whether the payment page is on screen. The server treats
   *   this poll as the liveness signal that releases the held slot when the
   *   app is killed, and a hidden page keeps the long window instead — it is
   *   usually a 3-D Secure challenge, not a member who left.
   */
  async getSession(id: string, visible = true): Promise<CheckoutSession> {
    return this.get<CheckoutSession>(
      `/sessions/${id}?visible=${visible ? 'true' : 'false'}`,
    );
  }

  /**
   * Back out of a checkout the member no longer wants.
   *
   * Voids the SmartLev invoice server-side. Without it the booking is
   * released while its invoice stays live and payable for the two or three
   * minutes until the expiry sweep catches up — long enough for a member to
   * open it from SmartLev and pay for a session that no longer exists.
   */
  async cancelSession(id: string): Promise<void> {
    await this.post<void>(`/sessions/${id}/cancel`, {});
  }

  /**
   * Tell the server the payment page just went off screen, on the way out.
   *
   * `keepalive` rather than the usual client: this fires from
   * `visibilitychange`, the last moment before iOS suspends the WebView, and
   * an ordinary request is abandoned when that happens. A keepalive request is
   * handed to the platform to finish on its own.
   *
   * Missing it is not a disaster — the member simply keeps the shorter
   * foreground window — so nothing here throws or is awaited.
   */
  reportHidden(id: string): void {
    if (isDemoMode) return;
    try {
      const token =
        localStorage.getItem('token') ?? '';
      const base =
        import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      void fetch(`${base}/checkout/sessions/${id}?visible=false`, {
        method: 'GET',
        keepalive: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      }).catch(() => undefined);
    } catch {
      /* A page on its way out is not somewhere to surface errors. */
    }
  }
}

export const checkoutService = new CheckoutService();
export default checkoutService;
